export function isOnline(): boolean {
  return navigator.onLine;
}

export function handleNetworkError(err: any): string {
  // Prefer real network failure signals over navigator.onLine (unreliable in Chrome).
  if (
    err?.code === "ERR_NAME_NOT_RESOLVED" ||
    err?.code === "ENOTFOUND" ||
    err?.message?.includes("Failed to fetch") ||
    err?.message?.includes("Network Error")
  ) {
    return "You appear to be offline. Check your connection and try again.";
  }

  if (
    err?.response?.status === 504 ||
    err?.code === "ECONNABORTED" ||
    err?.message?.includes("Request has been terminated")
  ) {
    return "This is taking longer than usual. Please try again.";
  }

  if (err?.response?.status === 502) {
    return "Service is temporarily unavailable. Please try again.";
  }

  if (err?.response?.status === 503) {
    return "Service is temporarily unavailable. Please try again.";
  }

  // AC6 – Rate limiting
  if (err?.response?.status === 429) {
    return "Too many attempts. Please wait a moment and try again.";
  }

  if (err?.response?.status === 403) {
    return "You don’t have permission to perform this action.";
  }

  return err;
}

/** True when the API text is a raw runtime dump, not a customer-facing message. */
function isTechnicalErrorText(message: string): boolean {
  const s = message.toLowerCase();
  return (
    s.includes("object reference not set") ||
    s.includes("nullreferenceexception") ||
    s.includes("invalid json") ||
    s.includes("status_code") ||
    s.includes("stack trace") ||
    s.includes("at furp.") ||
    s.includes("system.nullreference") ||
    /\bunhandled exception\b/.test(s) ||
    /\bunexpected token\b/.test(s)
  );
}

export function sanitizeErrorMessage(err: unknown): unknown {
  if (!err) {
    return "Something went wrong, please try again later.";
  }

  let message: string;

  if (typeof err === "string") {
    message = err;
  } else if (err && typeof (err as any)?.message === "string") {
    message = (err as any).message;
  } else {
    return err;
  }

  if (isTechnicalErrorText(message)) {
    return "Something went wrong, please try again later.";
  }

  return message;
}

/** Toast-safe string from RTK / apiClient rejections (string or { message }). */
export function errorToastText(
  error: unknown,
  fallback = "Something went wrong, please try again later.",
): string {
  if (typeof error === "string" && error.trim()) return error;
  if (error && typeof (error as any).message === "string") {
    const msg = String((error as any).message).trim();
    if (msg) return msg;
  }
  return fallback;
}
